import { motion, useInView, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";

/* ─────────────────────────────────────────────
   REVEAL — blur + fade + lift on viewport entry
───────────────────────────────────────────── */
export function Reveal({ children, delay = 0, y = 28, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reduce = useReducedMotion();
  return (
    <motion.div
      ref={ref}
      initial={reduce ? false : { opacity: 0, y, filter: "blur(6px)" }}
      animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
      transition={{ duration: 0.85, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   STAGGER GROUP
───────────────────────────────────────────── */
export function StaggerGroup({ children, stagger = 0.08, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: stagger } },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, y = 24, className = "" }) {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y, filter: "blur(4px)" },
        show: {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   MAGNETIC — subtle cursor pull
───────────────────────────────────────────── */
export function Magnetic({ children, strength = 0.22, className = "" }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) return;
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = e.clientX - (r.left + r.width / 2);
      const y = e.clientY - (r.top + r.height / 2);
      el.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
    };
    const onLeave = () => {
      el.style.transform = "translate(0,0)";
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [strength, reduce]);

  return (
    <span ref={ref} className={`inline-block transition-transform duration-300 ease-out ${className}`}>
      {children}
    </span>
  );
}

/* ─────────────────────────────────────────────
   COUNTER — animated number
───────────────────────────────────────────── */
export function Counter({ to, suffix = "", duration = 1400, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (!inView) return;
    if (reduce) { setVal(to); return; }
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      setVal(Math.floor(eased * to));
      if (p < 1) requestAnimationFrame(tick);
      else setVal(to);
    };
    requestAnimationFrame(tick);
  }, [inView, to, duration, reduce]);

  const formatted = val >= 1000 ? val.toLocaleString("en-IN") : val;
  return (
    <span ref={ref} className={className}>
      {formatted}{suffix}
    </span>
  );
}

/* ─────────────────────────────────────────────
   WORD REVEAL — word-by-word lift
───────────────────────────────────────────── */
export function WordReveal({ text, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const reduce = useReducedMotion();
  const words = text.split(" ");
  return (
    <span ref={ref} className={className}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom mr-[0.25em]">
          <motion.span
            className="inline-block"
            initial={reduce ? false : { y: "105%", opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: delay + i * 0.045, ease: [0.16, 1, 0.3, 1] }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

/* ─────────────────────────────────────────────
   MARQUEE — infinite horizontal scroll
───────────────────────────────────────────── */
export function Marquee({ children, speed = 40, className = "" }) {
  return (
    <div className={`marquee-wrap ${className}`}>
      <div className="marquee-track" style={{ animationDuration: `${speed}s` }}>
        {children}
        {children}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SHINY TEXT — coral wave shimmer
───────────────────────────────────────────── */
export function ShinyText({ children, className = "" }) {
  return <span className={`shiny-text ${className}`}>{children}</span>;
}

/* ─────────────────────────────────────────────
   PARALLAX SECTION — scroll-driven Y shift
───────────────────────────────────────────── */
export function ParallaxSection({ children, offset = 60, className = "" }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [offset, -offset]);

  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div ref={ref} style={{ y }} className={className}>
      {children}
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   GLOW CARD — hover glow wrapper
───────────────────────────────────────────── */
export function GlowCard({ children, className = "" }) {
  return (
    <motion.div
      className={`glass-card ${className}`}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   FLOATING BLOB — ambient background orb
───────────────────────────────────────────── */
export function FloatingBlob({ color = "aurora-coral", size = 400, top, left, right, bottom, delay = 0 }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={`aurora-blob ${color}`}
      style={{
        width: size,
        height: size,
        top,
        left,
        right,
        bottom,
      }}
      initial={reduce ? false : { opacity: 0, scale: 0.8 }}
      animate={reduce ? false : { opacity: 1, scale: 1 }}
      transition={{ duration: 2, delay, ease: "easeOut" }}
    />
  );
}

/* ─────────────────────────────────────────────
   ANIMATED LINE — decorative reveal line
───────────────────────────────────────────── */
export function AnimatedLine({ className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <motion.div
      ref={ref}
      className={`h-px bg-gradient-to-r from-transparent via-[var(--color-coral)] to-transparent ${className}`}
      initial={{ scaleX: 0, opacity: 0 }}
      animate={inView ? { scaleX: 1, opacity: 1 } : {}}
      transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
      style={{ transformOrigin: "left" }}
    />
  );
}
